import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ErrorResponse, ErrorResponseType, SurveySchemaType} from '../utils/surveyStatusSchemas';
import { v1SurveySchema } from '../utils/QuestionnaireFlows/questionnaireBase';
import { useErrorState } from './useErrorState';

export type AppStateType = {
    apiUrl?: string;
    surveyId?: string;
    token?: string;
    authError: Error | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    processResponse: (res: Response) => any;
    setSurveyId: (id: string) => void;
    setToken: (token: string) => void;
    getSurveySchema: () => Promise<SurveySchemaType>;
};

export const useAppState = create(
    persist<AppStateType>(
        (set) => ({
            apiUrl: undefined,
            surveyId: "",
            token: "",
            authError: undefined,
            setSurveyId: (id: string) => set({ surveyId: id }),
            setToken: (token: string) => set({ token: token }),
            getSurveySchema: async (): Promise<SurveySchemaType> => {
                return new Promise(resolve => resolve(v1SurveySchema as SurveySchemaType));
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            processResponse: async (res: Response): Promise<any> => {
                let data = null;
                try {
                    // Only attempt to parse JSON if the response has content
                    if (res.headers.get("content-type")?.includes("application/json")) {
                        data = await res.json();
                    }
                } catch (error) {
                    // If JSON parsing fails, throw an error
                    throw new Error("Failed to parse response body as JSON");
                }

                if (res.ok) {
                    return data;
                } else {
                    if (res.status === 401) {
                        set({ authError: new Error(res.statusText) });
                    } else {
                        if (ErrorResponse.safeParse(data).success) {
                            useErrorState.getState().addError({
                                title: 'Error getting data',
                                correlationId: (data as ErrorResponseType).correlationId,
                                message: (data as ErrorResponseType).errorMessage,
                                statusCode: (data as ErrorResponseType).statusCode,
                            });
                        }
                    }

                    throw new Error(res.statusText);
                }
            },
        }),
        {
            name: 'general',
            storage: createJSONStorage(() => sessionStorage),
        },
    )
);

export const getAppState = useAppState.getState;
